import { api } from './client';
import type { CheckoutHandoff, CheckoutResult, PaidOrder } from './payments';

/**
 * Keeping the Pro who is already in the house for a while longer.
 *
 * ---------------------------------------------------------------------------
 * WHY THIS IS NOT A SECOND BOOKING
 * ---------------------------------------------------------------------------
 * Booking another hour is the same wish with a worse ending: a new job goes to
 * dispatch, which may send somebody else, and the Pro standing in the kitchen
 * leaves at the time on the first one. This asks the server to keep THIS Pro,
 * which it can only answer by looking at what that Pro is doing next.
 *
 * So `request` is not a write the app can predict the result of. It comes back
 * in one of two states and the screen has to render both:
 *
 *   awaiting_payment  a Pro is held. Pay before `holdExpiresAt` or it lapses.
 *   awaiting_admin    nobody could be freed automatically. Homingo has until
 *                     `adminDeadlineAt` to arrange one; the answer arrives as
 *                     a push, and `list` is what a phone that missed it reads.
 *
 * Nothing is charged until the customer pays, and nothing changes on the
 * booking until that payment is captured - `slotEndAt` moves then and not
 * before.
 */
export type Extension = {
  id: string;
  bookingId: string;
  /** The time bought, in minutes: 15, 30, 45, 60, 90 - whatever was asked. */
  minutes: number;
  /** A decimal string, at the booking's own hourly rate - not today's price. */
  amount: string;
  status:
    | 'awaiting_admin'
    | 'awaiting_payment'
    | 'paid'
    | 'cancelled'
    | 'declined'
    | 'expired'
    | 'failed';
  /** The job's end as it stands, and as it would be once paid for. */
  fromEndAt: string;
  toEndAt: string;
  /** Pay by this instant, or the Pro is released. Null unless held. */
  holdExpiresAt: string | null;
  /** Homingo is arranging a Pro until this instant. Null unless waiting. */
  adminDeadlineAt: string | null;
  createdAt: string;
};

/** Open means the customer is still waiting on something. */
export const isOpen = (extension: Extension): boolean =>
  extension.status === 'awaiting_payment' ||
  extension.status === 'awaiting_admin';

export const requestExtension = (
  bookingId: string,
  minutes: number,
): Promise<Extension> =>
  api.post<Extension>(`/bookings/${encodeURIComponent(bookingId)}/extensions`, {
    minutes,
  });

/** Everything asked for on this booking, newest first. */
export const listExtensions = (bookingId: string): Promise<Extension[]> =>
  api.get<Extension[]>(`/bookings/${encodeURIComponent(bookingId)}/extensions`);

/**
 * Take the request back before paying for it.
 *
 * The one way out of a wrong choice: an open request holds the booking's only
 * slot, so without this a customer who picked an hour instead of fifteen
 * minutes waits out the hold before they can ask again.
 */
export const cancelExtension = (
  bookingId: string,
  extensionId: string,
): Promise<Extension> =>
  api.post<Extension>(
    `/bookings/${encodeURIComponent(bookingId)}/extensions/${encodeURIComponent(
      extensionId,
    )}/cancel`,
  );

/** The gateway order for held hours. Refused once the hold has lapsed. */
export const openExtensionCheckout = (
  bookingId: string,
  extensionId: string,
): Promise<CheckoutHandoff> =>
  api.post<CheckoutHandoff>(
    `/bookings/${encodeURIComponent(bookingId)}/extensions/${encodeURIComponent(
      extensionId,
    )}/payment/order`,
  );

/** Tell the server the payment went through; its capture adds the hours. */
export const confirmExtensionCheckout = (
  bookingId: string,
  extensionId: string,
  result: CheckoutResult,
): Promise<PaidOrder> =>
  api.post<PaidOrder>(
    `/bookings/${encodeURIComponent(bookingId)}/extensions/${encodeURIComponent(
      extensionId,
    )}/payment/verify`,
    result,
  );
